/**
 * In-memory queue + smart flush triggers for add-in telemetry. Designed for
 * dependency-injection so tests can substitute the outbox, the transport,
 * the clock, and the id generator — and so the production wiring can use
 * MSAL-backed bearer tokens without this file knowing about MSAL.
 *
 * Flush triggers (priority order):
 *   1. Size:      buffer reaches `maxBatch` events.
 *   2. Error:     an `error`-kind event was just enqueued (jumps the queue).
 *   3. Time:      `flushMs` elapses since the last flush.
 *   4. Unload:    `pagehide` listener (wired in main.tsx — uses sendBeacon).
 *   5. Reconnect: `online` listener (wired in main.tsx).
 */

import { generateUuidV4, getCurrentCorrelationId } from "./correlation";
import type { Outbox } from "./outbox";
import {
  TELEMETRY_SCHEMA_VERSION,
  isAllowedPropertyKey,
  type AllowedPropertyValue,
  type EventKind,
  type EventName,
  type TelemetryEvent,
  type TelemetryIdentity,
  type TelemetryProperties,
} from "./types";
import { ADDIN_VERSION } from "./version";

/** Reason a flush was triggered. Used for diagnostics and to gate sendBeacon. */
export type FlushReason = "size" | "timer" | "error" | "unload" | "reconnect" | "manual";

/** Wire interface for shipping a batch. Two paths so unload can use sendBeacon. */
export interface TelemetryTransport {
  /** Normal POST. Returns true on 2xx. Must NOT throw. */
  send(events: readonly TelemetryEvent[]): Promise<boolean>;

  /** Beacon path for pagehide. Synchronous fire-and-forget. */
  sendBeacon(events: readonly TelemetryEvent[]): boolean;
}

/** Identifier source for new events. Injectable for deterministic tests. */
export type EventIdFactory = () => string;

/** Clock source for `occurredAtUtc`. Injectable for deterministic tests. */
export type Clock = () => Date;

export interface TelemetryClientOptions {
  /** Durable backing store. Required. */
  outbox: Outbox;
  /** HTTP transport. Required. */
  transport: TelemetryTransport;

  // Tunables
  /** Flush when this many events are buffered. Default 20. */
  maxBatch?: number;
  /** Flush when this many ms have elapsed since the last flush. Default 5000. */
  flushMs?: number;
  /** Hard ceiling on the in-memory queue. Default 500 — drop-oldest beyond. */
  maxQueue?: number;

  // Test seams
  eventIdFactory?: EventIdFactory;
  clock?: Clock;
  /** Schedule a timer. Defaults to `setTimeout`/`clearTimeout`. */
  schedule?: {
    set: (cb: () => void, ms: number) => unknown;
    clear: (handle: unknown) => void;
  };
}

/**
 * Identity passed when the host has neither resolved settings nor detected
 * the Office.js diagnostics yet. The boot event ships with these defaults
 * and is overwritten as soon as {@link TelemetryClient.bindIdentity} is
 * called with the real values.
 */
const UNKNOWN_IDENTITY: TelemetryIdentity = {
  addInHost: "outlook", // safe default; rebound at boot
  // Known even before Office.js resolves — it is baked into the bundle rather
  // than detected from the host, so unlike the three fields below it is never
  // "unknown" and never needs rebinding.
  addInVersion: ADDIN_VERSION,
  officeJsVersion: "unknown",
  platform: "unknown",
};

const DEFAULT_MAX_BATCH = 20;
const DEFAULT_FLUSH_MS = 5_000;
const DEFAULT_MAX_QUEUE = 500;

export class TelemetryClient {
  private readonly outbox: Outbox;
  private readonly transport: TelemetryTransport;
  private readonly maxBatch: number;
  private readonly flushMs: number;
  private readonly maxQueue: number;
  private readonly eventIdFactory: EventIdFactory;
  private readonly clock: Clock;
  private readonly schedule: {
    set: (cb: () => void, ms: number) => unknown;
    clear: (handle: unknown) => void;
  };

  private readonly sessionId: string;
  private identity: TelemetryIdentity = { ...UNKNOWN_IDENTITY };
  private queue: TelemetryEvent[] = [];
  // `unknown` already includes `undefined`, so no union needed.
  private timerHandle: unknown;

  constructor(options: TelemetryClientOptions) {
    if (!options.outbox) throw new Error("TelemetryClient: outbox is required");
    if (!options.transport) throw new Error("TelemetryClient: transport is required");

    this.outbox = options.outbox;
    this.transport = options.transport;
    this.maxBatch = options.maxBatch ?? DEFAULT_MAX_BATCH;
    this.flushMs = options.flushMs ?? DEFAULT_FLUSH_MS;
    this.maxQueue = options.maxQueue ?? DEFAULT_MAX_QUEUE;
    this.eventIdFactory = options.eventIdFactory ?? defaultEventIdFactory;
    this.clock = options.clock ?? (() => new Date());
    this.schedule = options.schedule ?? {
      set: (cb, ms) => globalThis.setTimeout(cb, ms),
      clear: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
    };
    this.sessionId = this.eventIdFactory();
  }

  /** Bind the resolved user/tenant identity. Call once at boot after settings load. */
  bindIdentity(identity: Partial<TelemetryIdentity>): void {
    this.identity = { ...this.identity, ...identity };
  }

  /** Read-only snapshot of the currently-bound identity. Test seam. */
  getIdentity(): TelemetryIdentity {
    return { ...this.identity };
  }

  /** Session id assigned at construction. */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Enqueue a non-error event. Errors must go through {@link enqueueError}
   * so the priority-flush path fires.
   */
  enqueue(name: EventName, kind: Exclude<EventKind, "error">, properties?: TelemetryProperties): void {
    this.acceptEvent(this.build(name, kind, properties));
  }

  /** Enqueue an error event. Triggers an immediate flush. */
  enqueueError(
    name: EventName,
    error: NonNullable<TelemetryEvent["error"]>,
    properties?: TelemetryProperties,
  ): void {
    const event = this.build(name, "error", properties);
    event.error = error;
    this.acceptEvent(event);
  }

  /**
   * Flush the in-memory queue (and the outbox if {@link reason}=="reconnect").
   * Public so callers wiring `pagehide` / `online` events can trigger.
   */
  async flush(reason: FlushReason = "manual"): Promise<void> {
    if (this.timerHandle !== undefined) {
      this.schedule.clear(this.timerHandle);
      this.timerHandle = undefined;
    }

    const inMemory = this.queue;
    this.queue = [];

    // The reconnect path also drains the outbox: rows that survived a crash
    // get a chance to ship now that we know the network is back.
    const stragglers = reason === "reconnect" ? await this.safelyDrain() : [];
    const batch = [...stragglers, ...inMemory];

    if (batch.length === 0) return;

    let sent = false;
    if (reason === "unload") {
      // sendBeacon is fire-and-forget. We treat it as "best effort succeeded"
      // and clear the outbox; if the browser actually drops the request
      // we'll lose those events. Acceptable trade-off: the alternative is
      // losing them anyway when the page tears down.
      sent = this.transport.sendBeacon(batch);
    } else {
      try {
        sent = await this.transport.send(batch);
      } catch {
        sent = false;
      }
    }

    if (sent) {
      await this.safelyDelete(batch.map((e) => e.eventId));
    }
    // When `sent` is false the events stay in the outbox:
    //   - In-memory-only events were already persisted by `acceptEvent`,
    //     so the next reconnect/boot replays them.
    //   - Reconnect drains were already in the outbox; we just leave them
    //     there for a future attempt.
  }

  /** Drop everything in-memory and on disk. Test-only. */
  async _reset(): Promise<void> {
    this.queue = [];
    if (this.timerHandle !== undefined) {
      this.schedule.clear(this.timerHandle);
      this.timerHandle = undefined;
    }
    await this.outbox.clear();
  }

  // ── internals ──────────────────────────────────────────────────────────

  private build(
    name: EventName,
    kind: EventKind,
    properties: TelemetryProperties | undefined,
  ): TelemetryEvent {
    return {
      schemaVersion: TELEMETRY_SCHEMA_VERSION,
      eventId: this.eventIdFactory(),
      occurredAtUtc: this.clock().toISOString(),
      kind,
      name,
      addInHost: this.identity.addInHost,
      addInVersion: this.identity.addInVersion,
      officeJsVersion: this.identity.officeJsVersion,
      platform: this.identity.platform,
      // Present once the session is bound (see AuthStateProvider.applyTokens);
      // the key is omitted entirely on the pre-auth boot event, where the server
      // row falls back to "system".
      ...(this.identity.userEmail ? { userEmail: this.identity.userEmail } : {}),
      sessionId: this.sessionId,
      correlationId: getCurrentCorrelationId(),
      properties: filterProperties(properties),
    };
  }

  private acceptEvent(event: TelemetryEvent): void {
    // Push to the in-memory queue synchronously so a subsequent
    // _queueSnapshot() in tests (or a same-tick flush) sees the event
    // without waiting for the durable persist. The outbox put is fired
    // in the background and never blocks the producer thread.
    if (this.queue.length >= this.maxQueue) {
      this.queue.shift();
    }
    this.queue.push(event);

    // Background-persist. Best-effort: a failed outbox write only matters
    // if we later need to replay after a crash, and the alternative
    // (blocking the producer) would be strictly worse.
    void this.outbox.put(event).catch(() => {
      /* swallow — in-memory queue still has it for the next flush */
    });

    if (event.kind === "error") {
      void this.flush("error");
      return;
    }
    if (this.queue.length >= this.maxBatch) {
      void this.flush("size");
      return;
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.timerHandle !== undefined) return;
    this.timerHandle = this.schedule.set(() => {
      this.timerHandle = undefined;
      void this.flush("timer");
    }, this.flushMs);
  }

  private async safelyDrain(): Promise<TelemetryEvent[]> {
    try {
      return await this.outbox.drain();
    } catch {
      return [];
    }
  }

  private async safelyDelete(ids: readonly string[]): Promise<void> {
    try {
      await this.outbox.deleteByIds(ids);
    } catch {
      /* swallow */
    }
  }

  // Expose the queue snapshot for tests only. Returns a copy so external code
  // can't mutate the internal state.
  _queueSnapshot(): readonly TelemetryEvent[] {
    return [...this.queue];
  }
}

/**
 * Runtime allow-list filter — second line of defence in addition to the
 * TypeScript build-time gate.
 *
 * The build-time gate (`TelemetryProperties = Partial<Record<AllowedPropertyKey, ...>>`)
 * catches honest mistakes — a developer who types `customerName` sees an
 * IDE error and the build fails. But the type can be bypassed with `: any`,
 * `// @ts-ignore`, or `as unknown as TelemetryProperties`. This runtime
 * filter is the backstop: any key not on `ALLOWED_PROPERTY_KEYS` is silently
 * dropped before the event is queued or persisted. The filter is intentionally
 * silent — the build-time gate is the place to scream; the runtime filter is
 * for "type system was bypassed somehow" and "data crafted in dev tools".
 *
 * Exported (with the `_` prefix) so the tests can verify the filter in
 * isolation; not part of the public API.
 */
export function _filterProperties(
  input: TelemetryProperties | undefined,
): TelemetryProperties | undefined {
  if (!input) return undefined;
  // Iterate own enumerable keys. Cast through `Record<string, …>` because
  // `TelemetryProperties` is `Partial<Record<AllowedPropertyKey, ...>>` —
  // keys we filter out can't appear in that record type.
  const source = input as Record<string, AllowedPropertyValue | undefined>;
  let filtered: Record<string, AllowedPropertyValue> | undefined;
  for (const key of Object.keys(source)) {
    if (!isAllowedPropertyKey(key)) continue;
    const value = source[key];
    // Skip `undefined` values to keep the wire shape clean; the type system
    // could theoretically deliver them when `noUncheckedIndexedAccess` is on.
    if (value === undefined) continue;
    filtered ??= {};
    filtered[key] = value;
  }
  // `Record<string, AllowedPropertyValue>` is structurally assignable to
  // `TelemetryProperties` (= `Partial<Record<AllowedPropertyKey, ...>>`)
  // since every value we wrote into `filtered` passed the isAllowedPropertyKey
  // guard, so no cast is needed - the return type already accepts this shape.
  return filtered;
}

/** Internal alias so the `build()` method reads cleanly. */
const filterProperties = _filterProperties;

/**
 * Default event-id factory — delegates to `generateUuidV4` so we have a
 * single, audited UUID generator across the telemetry module (and so
 * `Math.random` doesn't appear on a security-adjacent path).
 */
const defaultEventIdFactory: EventIdFactory = generateUuidV4;

/** Picked up by tests via type assertion so they can pass the same default
 * factory deterministically. Not part of the public API. */
export const _defaultEventIdFactory: EventIdFactory = defaultEventIdFactory;

/** Public re-export so consumers don't have to import the symbol from types.ts. */
export type { AddInHost } from "./types";
